import { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadialBarChart, RadialBar, Legend
} from "recharts";

const CHART_COLORS = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316", "#ec4899"];

function countBullets(text, sectionHeader) {
  const regex = new RegExp(`## ${sectionHeader}[\\s\\S]*?(?=\\n## |$)`, "i");
  const match = text.match(regex);
  if (!match) return 0;
  return (match[0].match(/^[-*]\s/gm) || []).length;
}

function extractScore(text) {
  const scoreMatch = text.match(/(?:score|rating)[:\s]*(\d{1,3})\s*(?:\/\s*100|out of 100)?/i)
    || text.match(/(\d{1,3})\s*\/\s*100/i)
    || text.match(/## Priority Score[\s\S]*?(\d{1,3})/i);
  return scoreMatch ? Math.min(parseInt(scoreMatch[1], 10), 100) : null;
}

function extractSections(text) {
  const sections = [];
  const regex = /^## (.+)$/gm;
  let match;
  while ((match = regex.exec(text)) !== null) {
    sections.push({ title: match[1].trim(), index: match.index });
  }
  return sections.map((s, i) => {
    const end = i < sections.length - 1 ? sections[i + 1].index : text.length;
    const body = text.slice(s.index, end);
    const bullets = (body.match(/^[-*]\s/gm) || []).length;
    return { ...s, bullets, body };
  });
}

function LeadScoreGauge({ score }) {
  const data = [{ name: "Score", value: score, fill: score >= 70 ? "#10b981" : score >= 40 ? "#f59e0b" : "#ef4444" }];
  const label = score >= 70 ? "Hot Lead" : score >= 40 ? "Warm Lead" : "Cold Lead";
  return (
    <div className="flex flex-col items-center">
      <p className="text-xs font-medium text-slate-500 mb-1">Priority Score</p>
      <div className="w-36 h-36">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" startAngle={180} endAngle={0} data={data} barSize={14}>
            <RadialBar background clockWise dataKey="value" cornerRadius={8} max={100} />
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-2xl font-bold text-slate-800 -mt-10">{score}</p>
      <p className="text-xs font-medium mt-1" style={{ color: data[0].fill }}>{label}</p>
    </div>
  );
}

function SectionBarChart({ data, title }) {
  if (!data || data.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 mb-2">{title}</p>
      <ResponsiveContainer width="100%" height={Math.max(160, data.length * 32)}>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20, top: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: "#64748b" }} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function BreakdownPie({ data, title }) {
  if (!data || data.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 mb-2">{title}</p>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
            {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function ReportCharts({ content, mode }) {
  const charts = useMemo(() => {
    if (!content) return null;

    const sections = extractSections(content);

    if (mode === "LEAD_SCORING") {
      const score = extractScore(content);
      const highValue = countBullets(content, "High-Value Indicators");
      const redFlags = countBullets(content, "Red Flags");
      return { score, indicators: [
        { name: "High-Value Signals", value: highValue },
        { name: "Red Flags", value: redFlags },
      ]};
    }

    if (mode === "COMPETITOR_ANALYSIS") {
      return { sections: [
        { name: "Competitors", value: countBullets(content, "Typical Competitors") },
        { name: "Strengths", value: countBullets(content, "What They Do Well") },
        { name: "Weaknesses", value: countBullets(content, "Where They Fall Short") },
        { name: "Opportunities", value: countBullets(content, "Positioning Opportunities") },
      ].filter(d => d.value > 0) };
    }

    if (mode === "OPPORTUNITY_DETECTOR") {
      return { sections: [
        { name: "Missed Opportunities", value: countBullets(content, "Missed Opportunities") },
        { name: "Revenue Leaks", value: countBullets(content, "Revenue Leaks") },
        { name: "Quick Wins", value: countBullets(content, "Quick Wins") },
      ].filter(d => d.value > 0) };
    }

    if (mode === "OFFER_BUILDER") {
      return { sections: [
        { name: "Basic Package", value: countBullets(content, "Basic Package") },
        { name: "Standard Package", value: countBullets(content, "Standard Package") },
        { name: "Premium Package", value: countBullets(content, "Premium Package") },
      ].filter(d => d.value > 0) };
    }

    if (mode === "CALL_PREP") {
      return { sections: [
        { name: "Key Questions", value: countBullets(content, "Key Questions") },
        { name: "Objections", value: countBullets(content, "Likely Objections") },
      ].filter(d => d.value > 0) };
    }

    if (mode === "FULL_REPORT") {
      const sectionData = sections
        .filter(s => s.bullets > 0)
        .slice(0, 8)
        .map(s => ({ name: s.title.length > 22 ? s.title.slice(0, 20) + "..." : s.title, value: s.bullets }));
      return { sections: sectionData };
    }

    // Generic: section bullet count
    const sectionData = sections
      .filter(s => s.bullets > 0)
      .slice(0, 6)
      .map(s => ({ name: s.title.length > 22 ? s.title.slice(0, 20) + "..." : s.title, value: s.bullets }));
    return sectionData.length > 0 ? { sections: sectionData } : null;
  }, [content, mode]);

  if (!charts) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4 px-2 bg-slate-50/70 rounded-xl border border-slate-100" data-testid="report-charts">
      {charts.score != null && (
        <div className="flex justify-center items-center">
          <LeadScoreGauge score={charts.score} />
        </div>
      )}
      {charts.indicators && charts.indicators.length > 0 && (
        <SectionBarChart data={charts.indicators} title="Indicator Breakdown" />
      )}
      {charts.sections && charts.sections.length > 0 && (
        <>
          <SectionBarChart data={charts.sections} title="Insights by Section" />
          {charts.sections.length >= 3 && (
            <BreakdownPie data={charts.sections} title="Distribution" />
          )}
        </>
      )}
    </div>
  );
}
